import infoTokens from '@amzn/meridian-tokens/base/icon/info';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Popover from '@amzn/meridian/popover';
import Text from '@amzn/meridian/text';
import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';
import { COMMON_USAGE_MAP, getCardTypeMessageMap } from './helper';
import {
  ButtonPopoverProps,
  LighthouseMetricOption,
  TypeMessageMap,
} from './types';
import Divider from '@amzn/meridian/divider/divider';

const ButtonPopover: React.FC<ButtonPopoverProps> = ({
  type,
}: ButtonPopoverProps): React.ReactElement => {
  const buttonRef = useRef();
  const [open, setOpen] = useState(false);
  const onClickButton = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);

  const legacyExperience = !isFeatureEnabled(Feature.LIGHTHOUSE);

  const renderPopoverMessage = (
    type: string,
    cardTypeMessageMap: any,
    networkAvgMap: any,
    historicalMetricsInfo: any
  ): JSX.Element => {
    return (
      <Column spacingInset='none'>
        {/* Definition and Formula of metric */}
        {cardTypeMessageMap.titleMessage !== undefined &&
          cardTypeMessageMap.popoverMessage !== undefined && (
            <>
              <Text>
                <b>
                  {cardTypeMessageMap.subTitleMessage !== undefined ? (
                    <>
                      <FormattedMessage
                        id={cardTypeMessageMap.subTitleMessage.id}
                        defaultMessage={
                          cardTypeMessageMap.subTitleMessage.defaultMessage
                        }
                      />{' '}
                      (
                      <FormattedMessage
                        id={cardTypeMessageMap.titleMessage.id}
                        defaultMessage={
                          cardTypeMessageMap.titleMessage.defaultMessage
                        }
                      />
                      ){' '}
                    </>
                  ) : (
                    <>
                      <FormattedMessage
                        id={cardTypeMessageMap.titleMessage.id}
                        defaultMessage={
                          cardTypeMessageMap.titleMessage.defaultMessage
                        }
                      />{' '}
                    </>
                  )}
                </b>

                <FormattedMessage
                  id={cardTypeMessageMap.popoverMessage.definition.id}
                  defaultMessage={
                    cardTypeMessageMap.popoverMessage.definition.defaultMessage
                  }
                />
              </Text>
              <Text>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_formula'
                  defaultMessage='Formula:'
                />{' '}
                <FormattedMessage
                  id={cardTypeMessageMap.popoverMessage.formula.id}
                  defaultMessage={
                    cardTypeMessageMap.popoverMessage.formula.defaultMessage
                  }
                />
              </Text>
            </>
          )}

        {type === LighthouseMetricOption.QCS && (
          <Text>
            <FormattedMessage
              id={COMMON_USAGE_MAP.qcsContextOne.id}
              defaultMessage={COMMON_USAGE_MAP.qcsContextOne.defaultMessage}
            />{' '}
            <b>
              <FormattedMessage
                id={COMMON_USAGE_MAP.qcsContextTwo.id}
                defaultMessage={COMMON_USAGE_MAP.qcsContextTwo.defaultMessage}
              />
            </b>
          </Text>
        )}

        {/* Network Avg */}
        {type !== LighthouseMetricOption.QCS && networkAvgMap !== undefined && (
          <Text>
            <b>
              <FormattedMessage
                id={networkAvgMap.popoverMessage.id}
                defaultMessage={networkAvgMap.popoverMessage.defaultMessage}
              />{' '}
            </b>
            {networkAvgMap !== undefined && (
              <FormattedMessage
                id={networkAvgMap.definition.id}
                defaultMessage={networkAvgMap.definition.defaultMessage}
              />
            )}
          </Text>
        )}
        {isFeatureEnabled(Feature.HISTORICAL_METRICS) && (
          <>
            <Divider />
            <br />
            <FormattedMessage
              id={historicalMetricsInfo.id}
              defaultMessage={historicalMetricsInfo.defaultMessage}
            />
          </>
        )}
      </Column>
    );
  };

  const adjustedClassName =
    type === 'QCS' || type === 'QCS_RAP'
      ? ''
      : 'lighthouse-metric-info-popover';

  return (
    <>
      {!legacyExperience && (
        <>
          <Button
            type='icon'
            onClick={onClickButton}
            ref={buttonRef}
            data-testid='metric-popover-button'
            circular
            size='small'
            className={adjustedClassName}
          >
            <Icon tokens={infoTokens} />
          </Button>
          <Popover
            anchorNode={buttonRef.current}
            open={open}
            onClose={onClose}
            position='end'
            maxWidth='387px'
          >
            {renderPopoverMessage(
              type,
              getCardTypeMessageMap(type),
              COMMON_USAGE_MAP.networkAverage,
              COMMON_USAGE_MAP.historicalTeamLevelMetricsInformation
            )}
          </Popover>
        </>
      )}
    </>
  );
};

export default ButtonPopover;
